import React from 'react'
import { Button as MuiButton, ButtonProps as MuiButtonProps, CircularProgress } from '@mui/material'
import { styled } from '@mui/material/styles'

export type DsButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'spectrum' | 'night'
export type DsButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'color' | 'size'> {
  variant?: DsButtonVariant
  size?: DsButtonSize
  loading?: boolean
}

const sizeStyles: Record<DsButtonSize, React.CSSProperties> = {
  sm: { height: 34, minHeight: 34, paddingLeft: 15, paddingRight: 15, fontSize: 13 },
  md: { height: 42, minHeight: 42, paddingLeft: 20, paddingRight: 20, fontSize: 14 },
  lg: { height: 50, minHeight: 50, paddingLeft: 28, paddingRight: 28, fontSize: 15 },
}

// Mirrors the mockup's `.btn` disabled treatment — opacity fade only, never
// MUI's default flat gray override. `bg`/`color` restate the variant's own
// resting colors so `&.Mui-disabled` (higher specificity than our base) keeps
// the pill's identity instead of losing it.
function disabledFade(bg: string, color: string) {
  return {
    '&.Mui-disabled': {
      backgroundColor: bg,
      color,
      opacity: 0.4,
      pointerEvents: 'none' as const,
    },
  }
}

const Root = styled(MuiButton, {
  shouldForwardProp: (p) => p !== 'dsVariant' && p !== 'dsSize',
})<{ dsVariant: DsButtonVariant; dsSize: DsButtonSize }>(({ dsVariant, dsSize }) => {
  const base = {
    ...sizeStyles[dsSize],
    borderRadius: 'var(--ds-radius-pill)',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    lineHeight: 1,
    textTransform: 'none' as const,
    whiteSpace: 'nowrap' as const,
    transition: [
      `transform var(--ds-duration-fast) var(--ds-ease)`,
      `background var(--ds-duration-normal) var(--ds-ease)`,
      `opacity var(--ds-duration-normal) var(--ds-ease)`,
      `color var(--ds-duration-fast) var(--ds-ease)`,
    ].join(', '),
    '&:active': { transform: 'scale(0.975)' },
  }

  if (dsVariant === 'primary') {
    return {
      ...base,
      backgroundColor: 'var(--ds-inverse)',
      color: 'var(--ds-inverse-ink)',
      '&:hover': { backgroundColor: 'var(--ds-inverse)', opacity: 0.85 },
      ...disabledFade('var(--ds-inverse)', 'var(--ds-inverse-ink)'),
    }
  }
  if (dsVariant === 'spectrum') {
    return {
      ...base,
      position: 'relative' as const,
      isolation: 'isolate' as const,
      backgroundColor: 'var(--ds-inverse)',
      color: 'var(--ds-inverse-ink)',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: -2.5,
        borderRadius: 'var(--ds-radius-pill)',
        background: 'var(--ds-gradient-spectrum, var(--ds-inverse))',
        zIndex: -1,
        opacity: 0,
        transition: 'opacity var(--ds-duration-normal) var(--ds-ease)',
      },
      '&:hover::before': { opacity: 1 },
      '&:hover': { backgroundColor: 'var(--ds-inverse)' },
      ...disabledFade('var(--ds-inverse)', 'var(--ds-inverse-ink)'),
    }
  }
  if (dsVariant === 'secondary') {
    return {
      ...base,
      backgroundColor: 'var(--ds-canvas)',
      color: 'var(--ds-ink)',
      boxShadow: 'inset 0 0 0 1px var(--ds-border-strong)',
      '&:hover': { boxShadow: 'inset 0 0 0 1px var(--ds-ink)' },
      ...disabledFade('var(--ds-canvas)', 'var(--ds-ink)'),
    }
  }
  if (dsVariant === 'danger') {
    return {
      ...base,
      backgroundColor: 'var(--ds-danger)',
      color: 'var(--ds-inverse-ink)',
      '&:hover': { backgroundColor: 'var(--ds-danger)', opacity: 0.9 },
      ...disabledFade('var(--ds-danger)', 'var(--ds-inverse-ink)'),
    }
  }
  if (dsVariant === 'night') {
    // Glass chrome for dark/immersive surfaces — lightbox top bar, gallery studio.
    return {
      ...base,
      backgroundColor: 'rgba(244,243,246,0.1)',
      color: 'var(--ds-night-ink, #f4f3f6)',
      boxShadow: 'inset 0 0 0 1px var(--ds-night-border, rgba(244,243,246,0.1))',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      '&:hover': { backgroundColor: 'rgba(244,243,246,0.16)' },
      ...disabledFade('rgba(244,243,246,0.1)', 'var(--ds-night-ink, #f4f3f6)'),
    }
  }
  // ghost — muted ink, dips on hover (matches .btn-ghost)
  return {
    ...base,
    backgroundColor: 'transparent',
    color: 'var(--ds-ink-muted)',
    '&:hover': { color: 'var(--ds-ink)', backgroundColor: 'var(--ds-surface)' },
    ...disabledFade('transparent', 'var(--ds-ink-muted)'),
  }
})

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, disabled, children, startIcon, ...rest },
  ref,
) {
  return (
    <Root
      ref={ref}
      dsVariant={variant}
      dsSize={size}
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={16} color='inherit' /> : startIcon}
      {...rest}
    >
      {children}
    </Root>
  )
})
